import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { IconButton, InputAdornment, InputLabel, OutlinedInput, FormControl, Typography, styled } from "@mui/material";
import { Stack, useTheme } from "@mui/system";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { passwordRegex } from "constants/regex";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RoutePaths } from "constants/RoutePaths";
import { useRegisterInvitedUser } from "hooks/useRegisterInvitedUser";
import { InitPageContainerNoFooter } from "components/views/InitPageContainerNoFooter/InitPageContainerNoFooter";
import ErrorIcon from '@mui/icons-material/Error';
import { useGetInvitationStatus } from "hooks/useGetInvitationStatus";
import { InvitationStatus } from "api/models/InvitationStatus";
import { SecondaryTextBox, DisplayNameInfoTypography, StyledOutlinedInput, PolicyAgreementStack, SubmitButton } from './FinishRegistrationPage.styles';
import { IssCheckbox } from "components/IssCheckBox";
import { createPrefixedPropsFilteredComponent } from 'helpers/styledHelpers';
import { createColorFilteredComponent } from 'helpers/styledHelpers';

const StyledErrorContainer = createPrefixedPropsFilteredComponent<{ $color: string; $marginTop: string; $marginBottom: string }>('div')(({ $color, $marginTop, $marginBottom }) => ({
  display: 'flex',
  alignItems: 'center',
  color: $color,
  marginTop: $marginTop,
  marginBottom: $marginBottom
}));

const StyledErrorTypography = styled(Typography)({
  marginLeft: '8px'
});

const StyledSimpleErrorDiv = createColorFilteredComponent<{ $color: string }>('div')(({ $color }) => ({
  color: $color
}));

const StyledPrivaceErrorDiv = createColorFilteredComponent<{ $color: string }>('div')(({ $color }) => ({
  color: $color,
  marginLeft: '1.6rem'
}));

export const FinishRegistrationPage = () => {
    const [displayName, setDisplayName] = useState('');
    const [password, setPassword] = useState('');
    const [passwordRepeated, setPasswordRepeated] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordRepeatedError, setPasswordRepeatedError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordRepeated, setShowPasswordRepeated] = useState(false);    
    const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
    const [showPrivacyPolicyError, setShowPrivacyPolicyError] = useState(false);        
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { registerInvitedUser, registerInvitedUserPending } = useRegisterInvitedUser();
    const { fetchInvitationStatus } = useGetInvitationStatus();

    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const invitationId = queryParams.get('invitationId');
    const language = queryParams.get('language');

    useEffect(() => {
        if (language) {
            i18n.changeLanguage(language);
        } else {
            i18n.changeLanguage('pl');
        }
    }, [language, i18n]);

    const isValidPassword = (password: string): boolean => {
        return passwordRegex.test(password);
    };

    const validatePassword = () => {
        if (isValidPassword(password) === false) {
            setPasswordError(t('finishRegistrationPage.invalidPassword'));
            return false;
          }
          setPasswordError('');
          return true;
    }

    const validatePasswordRepeated = () => {
        if (isValidPassword(passwordRepeated) === false) {
            setPasswordRepeatedError(t('finishRegistrationPage.invalidPassword'));
            return false;
        }

        if (passwordRepeated !== password) {
            setPasswordRepeatedError(t('finishRegistrationPage.passwordsDoNotMatch'));
            return false;
        }

        setPasswordRepeatedError('');
        return true;
    }

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (event: any) => event.preventDefault();

    const handleClickShowPasswordRepeated = () => setShowPasswordRepeated(!showPasswordRepeated);
    const handleMouseDownPasswordRepeated = (event: any) => event.preventDefault();

    const handleOnAccepPrivacyPolicyCheckChanged = (checked: boolean) => {
      setAcceptPrivacyPolicy(checked);
      setShowPrivacyPolicyError(!checked);
    }

    const handleSubmit = async () => {
        const isPasswordValid = validatePassword();
        const isPasswordRepeatedValid = validatePasswordRepeated();

        if(!acceptPrivacyPolicy) {
          setShowPrivacyPolicyError(true);
        }

        if (isPasswordValid && isPasswordRepeatedValid && token && invitationId && acceptPrivacyPolicy) {
            const resp = await registerInvitedUser({
                displayName: displayName,
                password: password,
                token: token,
                invitationId: invitationId,
                acceptPrivacyPolicy: acceptPrivacyPolicy
            });

            if (resp.success) {
                navigate(RoutePaths.LOGIN);
            }
        }
    }

    useEffect(() => {
      const checkInvitationStatus = async (invitationId: string) => {
        const resp = await fetchInvitationStatus(invitationId);
        if (resp && resp.data) {
          const status = resp.data as InvitationStatus;
          if (status !== InvitationStatus.Available) {
            navigate(RoutePaths.INVITATION_ERROR);
          }
        }
      }

      if (invitationId) {
        checkInvitationStatus(invitationId)
      }
    }, [invitationId, fetchInvitationStatus, navigate]);

    const genertatePrivacyPolicyLink = () => {
      return (<PolicyAgreementStack>
        <Typography textAlign={'left'} width={'100%'} variant="body2">
            <Link to={RoutePaths.PRIVACY_POLICY}>
                {' ' + t('finishRegistrationPage.policyAgreementPart4')}
            </Link>
            <Link to={RoutePaths.PRIVACY_POLICY}>
            </Link>
        </Typography>
      </PolicyAgreementStack>)
    }

    return <InitPageContainerNoFooter title={t('finishRegistrationPage.finishRegistration')} data-testid="finish-registration-page" cardHeight="700px">
        <Stack spacing={theme.spacing(2)}>        
            {passwordRepeatedError && <StyledErrorContainer $color={theme.palette.red.main} $marginTop={theme.spacing(-5)} $marginBottom={theme.spacing(4)}>
              <ErrorIcon />
              <StyledErrorTypography variant="body1">
                  {t(passwordRepeatedError)}
              </StyledErrorTypography>
            </StyledErrorContainer>}
            <FormControl variant="outlined">
              <InputLabel htmlFor="displayName">{t('general.displayName')}</InputLabel>
              <OutlinedInput
                data-testid="finish-registration-fullname-input"
                label={t('general.displayName')}
                inputProps={{ id: 'displayName', 'aria-labelledby': 'displayName-label' }}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </FormControl>
            <SecondaryTextBox>
              <DisplayNameInfoTypography variant="body2" fontWeight={'400'} textAlign={'center'}>{t('finishRegistrationPage.displayNameWillBeVisible')}</DisplayNameInfoTypography>
            </SecondaryTextBox>
            <FormControl variant="outlined">
              <InputLabel htmlFor="password">{t('general.password')}</InputLabel>
              <StyledOutlinedInput
                data-testid="finish-registration-password-input"
                id="password"
                label={t('general.password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={validatePassword}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {passwordError && <StyledSimpleErrorDiv $color={theme.palette.red.main}>{passwordError}</StyledSimpleErrorDiv>}
              </FormControl>
              <FormControl variant="outlined">
              <InputLabel htmlFor="passwordRepeated">{t('finishRegistratioNpage.passwordRepeat')}</InputLabel>
              <OutlinedInput
                data-testid="finish-registration-password-repeated-input"
                id="passwordRepeated"
                label={t('finishRegistratioNpage.passwordRepeat')}
                type={showPasswordRepeated ? 'text' : 'password'}
                value={passwordRepeated}
                onChange={(e) => setPasswordRepeated(e.target.value)}
                onBlur={validatePasswordRepeated}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPasswordRepeated} onMouseDown={handleMouseDownPasswordRepeated} edge="end">
                      {showPasswordRepeated ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              </FormControl>
              <SecondaryTextBox>
                <Typography variant="body2">{t('finishRegistrationPage.passwordRequirements')}</Typography>
              </SecondaryTextBox>              
              <div>
                <IssCheckbox onHandleChanged={handleOnAccepPrivacyPolicyCheckChanged} 
                             description={t('finishRegistrationPage.acceptPolicyPrivacy')}
                             additionalText={genertatePrivacyPolicyLink()}
                />
                {showPrivacyPolicyError && 
                <StyledPrivaceErrorDiv $color={theme.palette.red.main}>
                  {t('general.required')}
                </StyledPrivaceErrorDiv>}
              </div>
            <Stack width={'100%'} display={'flex'} justifyContent={'center'}>
              <SubmitButton
                data-testid="finish-registration-submit-button" 
                color='primary' 
                type='submit' 
                loading={registerInvitedUserPending} 
                variant='contained' 
                onClick={handleSubmit}>
                  {t('finishRegistrationPage.createAccount')}
              </SubmitButton>              
            </Stack>
        </Stack>
    </InitPageContainerNoFooter>
}